/**
 * Chip for a linked session / patient / library document.
 *
 * Relocated out of `src/components/chat/components/ContextLinkingCommandLists.tsx`
 * — history: `git log --follow` that file. It is rendered by the Evidence chat
 * composer, the Work-home composer and the agent-chat session artifact header,
 * and it reads no state of its own: every caller already holds the linked
 * entity and passes it in.
 */

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import {
  FileOutput,
  FileText,
  FolderOpen,
  ListTodo,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';

interface LinkedContextBadgeProps {
  type: 'session' | 'patient' | 'library_document' | 'task' | 'collection';
  name: string;
  /** Secondary text shown below the name */
  subtitle?: string;
  onRemove: () => void;
  className?: string;
  /** Whether context linking is locked (chat has started, can't remove) */
  isContextLocked?: boolean;
  /**
   * Optional click handler invoked when the body of the badge is activated
   * (anything outside the Remove "X" button). Wiring this turns the badge
   * into a button that opens the side-panel preview.
   */
  onClick?: () => void;
  /**
   * When `true`, renders the badge in a pressed/active state to communicate
   * that the side-panel preview is currently showing this badge's content.
   */
  isActive?: boolean;
}

const LINKED_CONTEXT_ICONS: Record<LinkedContextBadgeProps['type'], LucideIcon> = {
  session: FileText,
  patient: UserRound,
  library_document: FileOutput,
  task: ListTodo,
  collection: FolderOpen,
};

export const LinkedContextBadge = ({
  type,
  name,
  subtitle,
  onRemove,
  className,
  isContextLocked = false,
  onClick,
  isActive = false,
}: LinkedContextBadgeProps) => {
  const { t } = useTranslations();
  const Icon = LINKED_CONTEXT_ICONS[type];
  const isInteractive = !!onClick;

  // The remove control sits on top of the badge; stop event propagation so a
  // click on the "X" doesn't ALSO toggle the preview panel.
  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove();
  };

  const bodyClassName = cn(
    'group/badge relative inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-border bg-surface p-2 text-start',
    isInteractive &&
      'cursor-pointer transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
    isActive && 'border-border-focus bg-surface-hover',
    className
  );

  const innerContent = (
    <>
      {/* Icon container */}
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-tertiary">
        <Icon size={20} className="text-text-secondary" />
      </div>
      {/* Text container - height matches icon (32px): 14px name + 6px gap + 12px subtitle */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="truncate text-sm font-medium leading-none text-text-primary">
          {name}
        </span>
        {subtitle && (
          <span className="truncate text-xs leading-none text-text-secondary">
            {subtitle}
          </span>
        )}
      </div>
    </>
  );

  const removeButton = !isContextLocked ? (
    <Button
      type="button"
      variant="secondary"
      size="xxs"
      iconOnly
      onClick={handleRemoveClick}
      className="absolute -end-2 -top-2 size-5 min-w-5 rounded-full p-0 opacity-100 transition-opacity sm:opacity-0 sm:group-hover/badge:opacity-100"
      aria-label={
        type === 'session'
          ? t('chat.contextLinking.removeSession' as TranslationKey)
          : t('chat.contextLinking.removePatient' as TranslationKey)
      }
    >
      <X size={12} className="text-text-secondary" />
    </Button>
  ) : null;

  if (isInteractive) {
    const previewLabel = t(
      (type === 'session'
        ? 'chat.contextLinking.previewSession'
        : 'chat.contextLinking.previewPatient') as TranslationKey
    );
    // Use a div with role="button" rather than a native <button> to avoid
    // illegal nesting of the Remove <button> inside another button. Keyboard
    // semantics (Enter / Space) are forwarded manually.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    };
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-pressed={isActive}
        aria-label={`${previewLabel}: ${name}`}
        className={bodyClassName}
        data-testid="linked-context-badge"
        data-context-type={type}
      >
        {innerContent}
        {removeButton}
      </div>
    );
  }

  return (
    <div
      className={bodyClassName}
      data-testid="linked-context-badge"
      data-context-type={type}
    >
      {innerContent}
      {removeButton}
    </div>
  );
};
